import React, { useState } from "react";

import { Button, Card } from "@cofano/cck";
import { Box, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileContract } from "@fortawesome/pro-duotone-svg-icons";

import LinkableContractSelect from "view/contractsNew/components/selects/LinkableContractSelect";
import ContractDetailsDialog from "view/contractsNew/components/dialogs/ContractDetailsDialog";
import ContractCreateEditDialog from "view/contractsNew/components/dialogs/ContractCreateEditDialog";

import { hasPluginPermission, Language } from "imports/Utils";

import { DetailsBlock, Height } from "../Blocks";

import { } from "__generated__/graphql";
import { Contract, SalesCharter, PurchaseCharter } from "types/index";
import { ChildPropsFromPagination } from "types/commons";
import { useStoreState } from "ezStore";
import { CharterSubtype } from "__generated__/enums";
import { PurchaseCharterFromQuery } from "view/charters/PurchaseCharterDetails";
import { CharterContractFragment } from "__generated__/view/contractsNew/graphql/contract.fragments.generated";
import { GetContractsShortQuery } from "__generated__/view/contractsNew/graphql/contract.queries.generated";

type ContractCreateBlockProps = {
	charter: SalesCharter | PurchaseCharter |  PurchaseCharterFromQuery;
	contract?: Contract | CharterContractFragment;
	color: string;
	goBack: () => void;
	refetch: () => void;
};

type QueriedContract = ChildPropsFromPagination<GetContractsShortQuery, "contracts">;

const ContractCreateBlock = ({ charter, contract, color, goBack, refetch }: ContractCreateBlockProps) => {

	const [ dialogOpen, openDialog ] = useState(false);

	const [ detailsDialog, setDetailsDialog ] = useState<{ open: boolean, contract: QueriedContract | undefined }>({
		open: false,
		contract: undefined,
	});

	const session = useStoreState(store => store.session);

	const isSpotCharter = charter.subtype === CharterSubtype.Spot;
	const isTimeCharter = charter.subtype === CharterSubtype.Time;
	const isContractCharter = charter.subtype === CharterSubtype.Contract;

	return (
		<React.Fragment>
			<DetailsBlock height={Height.Fill}>
				<Card
					color={color}
					icon={<FontAwesomeIcon icon={faFileContract} style={{ color: color, height: 22, width: 22 }} />}
					actions={contract ? [ {
						icon: <ArrowBackIcon fontSize="small" />,
						onClick: () => goBack(),
					} ] : undefined}
					disablePadding>
					<Box sx={{ borderTop: 1, borderColor: "divider" }}>
						<div className="create-or-link-contract">
							{isSpotCharter && (
								<div>
									<Typography variant="h3" sx={{ fontSize: 14, mb: 1 }} color="textPrimary">
										{Language("CREATE_A_NEW_CONTRACT")}
									</Typography>

									<Button onClick={() => openDialog(true)} variant="outlined" size="small">
										{Language("CREATE")}
									</Button>
								</div>
							)}

							{(isTimeCharter || isContractCharter || hasPluginPermission(session, "newSpotCharterMayLinkWithExistingContract")) && (
								<div>
									<Typography variant="h3" sx={{ fontSize: 14, mb: 1 }} color="textPrimary">
										{Language("ADD_EXISTING_CONTRACT")}
									</Typography>

									<div style={{ marginBottom: 16 }}>
										<LinkableContractSelect
											additionalSearchOptions={{
												charterType: charter.type,
												charterSubtypes: [ charter.subtype ],
												creditorId: charter.creditor.id,
												debtorId: charter.debtor.id,
												validFrom: charter.validFrom,
												validUntil: charter.validUntil,
											}}
											getOptionDisabled={(option) => !!charter.allContracts?.find(it => it.id === option.id)}
											onChange={option => {
												setDetailsDialog({
													open: !!option,
													contract: option || undefined,
												});
											}}
											value={detailsDialog.contract} />

										<ContractDetailsDialog
											open={detailsDialog.open}
											onClose={() => {
												setDetailsDialog({
													open: false,
													contract: detailsDialog.contract,
												});
											}}
											contract={detailsDialog.contract}
											charter={charter}
											refetch={refetch} />
									</div>
								</div>
							)}
						</div>
					</Box>
				</Card>
			</DetailsBlock>

			<ContractCreateEditDialog
				open={dialogOpen}
				onClose={() => openDialog(false)}
				charter={charter}
				refetch={refetch}
			/>
		</React.Fragment>
	);
};

export default ContractCreateBlock;
